import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import fileDownload from "js-file-download";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { ClipLoader } from "react-spinners";

import api from "services/api";
import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";

import MatrizCotadoRealizado from "./MatrizCotadoRealizado";
import FaixaCobertura from "./FaixaCobertura";
import DetalheCelulaModal from "./DetalheCelulaModal";
import CoberturaTabela from "./CoberturaTabela";
import { BLOCOS, METRICAS, DESCRICAO_BLOCO } from "./metricas";
import { ORIGENS_COBERTURA, coberturaDaUrl, embarcadorDaUrl, paramsCobertura, semFiltroCobertura } from "./filtroCobertura";
import { QUEBRAS, VISOES, mesesDoPeriodo, somarMetricas } from "./periodos";
import { fmtBRL, fmtInt, fmtPct, fmtRazao } from "./formatos";
import "./styles.css";

// GoBID › Indicadores › Cotado × Realizado (épico PO1-3620).
// Padrão "explorar": matriz transportadora/UF × período com frete realizado,
// valor da mercadoria, frete cotado, notas × CT-es, realizado × tabela e
// cobertura de tabela. Lê ?desde=&ate=&quebra= da URL (cards da Torre, H6).

const BASE = "/indicadores/gobid/cotado-realizado";
const DATA = "YYYY-MM-DD";
const REGEX_DATA = /^\d{4}-\d{2}-\d{2}$/;
const MAX_MESES = 24;

const QUICK_RANGES = [
  { key: "month", label: "Mês atual" },
  { key: "30d", label: "30 dias", days: 29 },
  { key: "90d", label: "90 dias", days: 89 },
  { key: "180d", label: "180 dias", days: 179 },
  { key: "ano", label: "Ano atual" },
  { key: "12m", label: "Últimos 12 meses" },
];

function getQuickRangeDates(key) {
  const hoje = moment();
  if (key === "month") return { desde: hoje.clone().startOf("month").format(DATA), ate: hoje.format(DATA) };
  if (key === "ano") return { desde: hoje.clone().startOf("year").format(DATA), ate: hoje.format(DATA) };
  if (key === "12m") return { desde: hoje.clone().subtract(11, "months").startOf("month").format(DATA), ate: hoje.format(DATA) };
  const range = QUICK_RANGES.find((r) => r.key === key);
  const days = range && range.days != null ? range.days : 29;
  return { desde: hoje.clone().subtract(days, "days").format(DATA), ate: hoje.format(DATA) };
}

// ?desde=&ate=&quebra= (H6: os cards da Torre abrem a página com o período).
// ?bloco=cobertura&transportadora=&origem= (PO1-3659: link do alerta de cobertura de tabela).
function filtrosDaUrl(search) {
  const q = new URLSearchParams(search || "");
  const desde = q.get("desde");
  const ate = q.get("ate");
  const quebra = q.get("quebra");
  const bloco = q.get("bloco");
  const periodo = desde && ate && REGEX_DATA.test(desde) && REGEX_DATA.test(ate) && desde <= ate ? { desde, ate } : null;
  return {
    periodo,
    quebra: QUEBRAS.some((x) => x.key === quebra) ? quebra : null,
    bloco: BLOCOS.some((b) => b.key === bloco) ? bloco : null,
    cobertura: coberturaDaUrl(q),
  };
}


const TONE_COLOR = { success: "#00bf9f", danger: "#fd5d93", warning: "#ffb236", info: "#1d8cf8" };

const StatusCard = ({ label, value, helper, iconClassName, tone, onClick }) => (
  <div
    className={`cr-status-card${onClick ? " cr-status-card--click" : ""}`}
    style={{ "--cr-status-color": TONE_COLOR[tone] || TONE_COLOR.info }}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    <div className="cr-status-card__top">
      <span className="cr-status-card__label">{label}</span>
      {iconClassName ? (
        <span className="cr-status-card__icon">
          <i className={iconClassName} />
        </span>
      ) : null}
    </div>
    <div className="cr-status-card__value">{value}</div>
    {helper ? <div className="cr-status-card__helper">{helper}</div> : null}
  </div>
);

// Quebra "Região": soma as linhas de UF por macrorregião, em memória.
function agruparPorRegiao(linhasUf) {
  const mapa = new Map();
  (linhasUf || []).forEach((l) => {
    const regiao = l.regiao || "Sem região";
    if (!mapa.has(regiao)) mapa.set(regiao, { chave: regiao, rotulo: regiao, ehRegiao: true, meses: {}, anoAnterior: {} });
    const alvo = mapa.get(regiao);
    Object.keys(l.meses || {}).forEach((m) => {
      alvo.meses[m] = somarMetricas(alvo.meses[m], l.meses[m]);
    });
    Object.keys(l.anoAnterior || {}).forEach((m) => {
      const c = l.anoAnterior[m];
      if (c !== null && c !== undefined) alvo.anoAnterior[m] = somarMetricas(alvo.anoAnterior[m], c);
      else if (alvo.anoAnterior[m] === undefined) alvo.anoAnterior[m] = null;
    });
  });
  return [...mapa.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"));
}

const CHAVE_BLOCO = { realizado: "realizado", cotado: "cotado", notas: "notas", tabela: "tabela", cobertura: "cobertura" };

const CotadoRealizadoGoBID = () => {
  const { empresa, hasEmpresaSelection, optionsEmpresa, selectEmpresa } = useCompanies();
  const { addToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const empresaId = empresa && empresa.value;
  const daUrl = useRef(filtrosDaUrl(location.search));

  const [filtros, setFiltros] = useState(() => daUrl.current.periodo || getQuickRangeDates("ano"));
  const filtrosRef = useRef(filtros);
  const [quebra, setQuebra] = useState(() => daUrl.current.quebra || "transportadora");
  const [porRegiao, setPorRegiao] = useState(false);
  const [visao, setVisao] = useState("mes");
  const [bloco, setBloco] = useState(() => daUrl.current.bloco || "realizado");
  const [comparar, setComparar] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  // Filtro do bloco Cobertura vindo do link do alerta (transportadora matriz + origem).
  const [filtroCobertura, setFiltroCobertura] = useState(() => daUrl.current.cobertura);
  const filtroCoberturaRef = useRef(daUrl.current.cobertura);

  const [dados, setDados] = useState({});
  const [loading, setLoading] = useState({});
  const [erros, setErros] = useState({});
  const [consultado, setConsultado] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [modal, setModal] = useState({ aberto: false });
  // Período da última consulta: bloco, detalhe e export usam este, não o que
  // está digitado nos campos sem ter clicado em Consultar (QA 25/10).
  const [aplicado, setAplicado] = useState(filtros);
  const aplicadoRef = useRef(filtros);
  // Cada consulta ganha um número; resposta de uma consulta anterior é descartada.
  const rodadaRef = useRef(0);
  const blocoRef = useRef(bloco);

  useEffect(() => {
    filtrosRef.current = filtros;
  }, [filtros]);

  useEffect(() => {
    blocoRef.current = bloco;
  }, [bloco]);

  const mesesPeriodo = useMemo(() => {
    try {
      return mesesDoPeriodo(filtros.desde, filtros.ate);
    } catch (e) {
      return [];
    }
  }, [filtros.desde, filtros.ate]);

  const erroPeriodo = useMemo(() => {
    if (!filtros.desde || !filtros.ate) return 'Informe o período ("De" e "Até").';
    if (filtros.desde > filtros.ate) return '"De" deve ser menor ou igual a "Até".';
    if (mesesPeriodo.length > MAX_MESES) return `O período pode ter no máximo ${MAX_MESES} meses.`;
    return null;
  }, [filtros, mesesPeriodo]);

  const carregarBloco = useCallback(
    async (chave, activeFilters) => {
      const def = BLOCOS.find((b) => b.key === chave);
      if (!def || !empresaId) return null;
      const rodada = rodadaRef.current;
      const vigente = () => rodada === rodadaRef.current;
      setLoading((atual) => ({ ...atual, [chave]: true }));
      setErros((atual) => ({ ...atual, [chave]: null }));
      try {
        const extra = chave === "cobertura" ? paramsCobertura(filtroCoberturaRef.current) : {};
        const res = await api.get(`${BASE}/${def.endpoint}`, {
          params: { idEmbarcador: empresaId, desde: activeFilters.desde, ate: activeFilters.ate, ...extra },
          timeout: 180000,
        });
        if (!vigente()) return null;
        setDados((atual) => ({ ...atual, [chave]: res.data }));
        return res.data;
      } catch (requestError) {
        if (!vigente()) return null;
        const status = requestError.response && requestError.response.status;
        const msg =
          (requestError.response && requestError.response.data && requestError.response.data.error) ||
          (status === 403 ? "Indicador disponível para o produto GoBID." : `Erro ao carregar o bloco ${def.label}.`);
        setErros((atual) => ({ ...atual, [chave]: msg }));
        if (chave === "realizado") {
          addToast({ place: "tr", type: "danger", title: "Cotado × Realizado", description: msg, icon: "tim-icons icon-alert-circle-exc" });
        }
        return null;
      } finally {
        if (vigente()) setLoading((atual) => ({ ...atual, [chave]: false }));
      }
    },
    [empresaId, addToast]
  );

  // Consulta: realizado e cotado juntos (resumo); os outros blocos carregam ao abrir.
  const consultar = useCallback(
    async (activeFilters) => {
      if (!empresaId || !hasEmpresaSelection) return;
      if (!activeFilters.desde || !activeFilters.ate || activeFilters.desde > activeFilters.ate) return;
      rodadaRef.current += 1;
      aplicadoRef.current = activeFilters;
      setAplicado(activeFilters);
      setDados({});
      setErros({});
      setLoading({});
      setConsultado(true);
      // realizado e cotado sempre; o bloco aberto também, senão ele fica vazio
      // depois de uma nova consulta (o efeito do bloco não roda de novo).
      const chaves = ["realizado", "cotado"];
      const aberto = CHAVE_BLOCO[blocoRef.current];
      if (aberto && chaves.indexOf(aberto) === -1) chaves.push(aberto);
      await Promise.all(chaves.map((chave) => carregarBloco(chave, activeFilters)));
    },
    [empresaId, hasEmpresaSelection, carregarBloco]
  );

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setDados({});
      setConsultado(false);
      return;
    }
    consultar(filtrosRef.current);
  }, [empresaId, hasEmpresaSelection, consultar]);

  // Link do alerta com ?embarcador=: abre na empresa do aviso, se o usuário tem acesso a ela.
  useEffect(() => {
    const id = embarcadorDaUrl(new URLSearchParams(location.search));
    if (!id || !optionsEmpresa || Number(empresaId) === id) return;
    const opcao = optionsEmpresa.find((o) => Number(o && o.value) === id);
    if (opcao) selectEmpresa(opcao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, optionsEmpresa]);

  // Outro link aberto com a página já montada (sino → mesma rota, query nova): relê a URL.
  const primeiraUrl = useRef(true);
  useEffect(() => {
    if (primeiraUrl.current) {
      primeiraUrl.current = false;
      return;
    }
    const u = filtrosDaUrl(location.search);
    filtroCoberturaRef.current = u.cobertura;
    setFiltroCobertura(u.cobertura);
    if (u.bloco) {
      blocoRef.current = u.bloco;
      setBloco(u.bloco);
    }
    if (u.quebra) setQuebra(u.quebra);
    const periodo = u.periodo || filtrosRef.current;
    filtrosRef.current = periodo;
    setFiltros(periodo);
    consultar(periodo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Bloco aberto sem dado ainda: carrega.
  useEffect(() => {
    if (!consultado || !empresaId) return;
    const chave = CHAVE_BLOCO[bloco];
    if (dados[chave] === undefined && !loading[chave] && !erros[chave]) carregarBloco(chave, aplicadoRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloco, consultado, empresaId]);

  const applyQuickRange = (key) => {
    const next = getQuickRangeDates(key);
    setFiltros(next);
    consultar(next);
  };

  const handleExport = async () => {
    if (!empresaId) return;
    setExportando(true);
    try {
      const url = bloco === "cobertura" ? `${BASE}/cobertura-tabela/export` : `${BASE}/export`;
      const extra = bloco === "cobertura" ? paramsCobertura(filtroCobertura) : {};
      const res = await api.get(url, {
        params: { idEmbarcador: empresaId, desde: aplicado.desde, ate: aplicado.ate, quebra, ...extra },
        responseType: "blob",
        timeout: 180000,
      });
      const nome = bloco === "cobertura" ? "destinos-sem-tabela" : "cotado-realizado";
      fileDownload(res.data, `${nome}-${aplicado.desde}-a-${aplicado.ate}.xlsx`);
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Exportar XLSX", description: "Falha ao gerar a planilha.", icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setExportando(false);
    }
  };

  // ✕ do selo: tira o filtro do link e recarrega o bloco Cobertura completo.
  // Também tira o filtro da URL (senão volta no refresh); a troca de URL relê e reconsulta.
  const limparFiltroCobertura = () => {
    filtroCoberturaRef.current = null;
    setFiltroCobertura(null);
    const search = semFiltroCobertura(location.search);
    if (search !== (location.search || "")) {
      history.replace({ pathname: location.pathname, search });
      return;
    }
    setDados((atual) => ({ ...atual, cobertura: undefined }));
    carregarBloco("cobertura", aplicadoRef.current);
  };

  // Clique numa célula: abre o modal com os documentos daquela célula.
  const abrirDetalhe = useCallback(
    async (linha, grupo, metrica, celula) => {
      if (!metrica.detalhe || !empresaId) return;
      const params = {
        idEmbarcador: empresaId,
        desde: aplicado.desde,
        ate: aplicado.ate,
        bloco: metrica.detalhe.bloco,
        limit: 500,
      };
      if (grupo) params.mes = grupo.meses.join(",");
      if (metrica.detalhe.classe) params.classe = metrica.detalhe.classe;
      if (linha.ehRegiao) {
        params.quebra = "regiao";
        params.chave = linha.chave;
      } else if (quebra !== "total") {
        params.quebra = quebra;
        params.chave = linha.chave;
      }
      const titulo = `${metrica.label} · ${linha.rotulo}`;
      const subtitulo = `${grupo ? grupo.rotulo : "Período"} · ${moment(aplicado.desde).format("DD/MM/YYYY")} a ${moment(aplicado.ate).format("DD/MM/YYYY")}`;
      setModal({ aberto: true, titulo, subtitulo, tipo: metrica.detalhe.tipo, loading: true, erro: null, dados: null, mostrarSimulado: metrica.detalhe.bloco === "tabela" });
      try {
        const res = await api.get(`${BASE}/detalhe`, { params, timeout: 180000 });
        setModal((atual) => ({ ...atual, loading: false, dados: res.data }));
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message || "Erro ao carregar o detalhe";
        setModal((atual) => ({ ...atual, loading: false, erro: msg }));
      }
    },
    [empresaId, aplicado, quebra]
  );

  const fecharModal = () => setModal({ aberto: false });

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Este indicador usa o embarcador selecionado no topo para comparar o frete cotado no GoBID com o frete realizado nos CT-es."
        />
      </div>
    );
  }

  const realizado = dados.realizado;
  const cotado = dados.cotado;
  const dadosBloco = dados[CHAVE_BLOCO[bloco]];
  const carregandoBloco = Boolean(loading[CHAVE_BLOCO[bloco]]);
  const erroBloco = erros[CHAVE_BLOCO[bloco]];
  const carregandoResumo = Boolean(loading.realizado || loading.cotado);
  const totaisR = (realizado && realizado.totais) || null;
  const totaisC = (cotado && cotado.totais) || null;
  const pctFreteMercadoria = totaisR && Number(totaisR.valorMercadoria) > 0 ? (Number(totaisR.freteRealizado) / Number(totaisR.valorMercadoria)) * 100 : null;

  const activeRange = QUICK_RANGES.find((range) => {
    const r = getQuickRangeDates(range.key);
    return r.desde === filtros.desde && r.ate === filtros.ate;
  });
  const periodoLabel = filtros.desde && filtros.ate ? `${moment(filtros.desde).format("DD/MM/YYYY")} a ${moment(filtros.ate).format("DD/MM/YYYY")}` : "";

  let linhasMatriz = [];
  if (dadosBloco && dadosBloco.quebras) {
    linhasMatriz = dadosBloco.quebras[quebra] || [];
    if (quebra === "uf" && porRegiao) linhasMatriz = agruparPorRegiao(linhasMatriz);
  }
  const metricasBloco = METRICAS[bloco] || [];
  const mesesDados = (dadosBloco && dadosBloco.meses) || (realizado && realizado.meses) || mesesPeriodo;

  return (
    <div className="content">
      <Card className="card-everlog-shell" style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}>
        <CardHeader style={{ ...autopilotHeaderStyle, borderBottom: "none", padding: "1.65rem 1.65rem 1.45rem" }}>
          <div style={{ ...autopilotEyebrowStyle, marginBottom: "0.6rem" }}>GoBID · Indicadores</div>
          <h2 style={{ ...autopilotTitleStyle, margin: "0 0 0.55rem", fontSize: "2rem", fontWeight: 800, lineHeight: 1.15 }}>Cotado × Realizado</h2>
          <p style={{ ...autopilotDescriptionStyle, maxWidth: 860, marginBottom: 0, fontSize: "0.98rem", lineHeight: 1.65 }}>
            Frete cotado no GoBID contra o frete realizado nos CT-es, por transportadora, região e período, com o valor da
            mercadoria, as notas sem CT-e e o realizado contra a tabela. Clique numa célula para ver os documentos.
          </p>
        </CardHeader>
      </Card>

      <Card className="card-everlog-shell" style={{ ...autopilotCardStyle, marginTop: "1.25rem", background: "var(--everlog-card-hero-bg)" }}>
        <CardBody style={autopilotBodyStyle}>
          <div className="cr-busca">
            <div>
              <span className="cr-busca__title">Período</span>
              {periodoLabel ? (
                <span className="cr-busca__hint">
                  {periodoLabel}
                  {!activeRange ? " (personalizado)" : ""}
                </span>
              ) : null}
            </div>
            <div className="cr-pills">
              {QUICK_RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  className={`cr-pill${activeRange && activeRange.key === range.key ? " is-active" : ""}`}
                  disabled={carregandoResumo}
                  onClick={() => applyQuickRange(range.key)}
                >
                  {range.label}
                </button>
              ))}
            </div>
            <button type="button" className={`cr-busca__filtros${mostrarFiltros ? " is-open" : ""}`} onClick={() => setMostrarFiltros((v) => !v)} aria-expanded={mostrarFiltros}>
              <i className="tim-icons icon-settings-gear-63" />
              <span>Filtros</span>
            </button>
          </div>

          {mostrarFiltros ? (
            <section className="cr-advfilters">
              <Row className="align-items-end">
                <Col lg="3" md="6">
                  <FormGroup>
                    <Label>De</Label>
                    <Input type="date" value={filtros.desde} disabled={carregandoResumo} onChange={(e) => {
                      const valor = e.target.value;
                      setFiltros((atual) => ({ ...atual, desde: valor }));
                    }} />
                  </FormGroup>
                </Col>
                <Col lg="3" md="6">
                  <FormGroup>
                    <Label>Até</Label>
                    <Input type="date" value={filtros.ate} disabled={carregandoResumo} onChange={(e) => {
                      const valor = e.target.value;
                      setFiltros((atual) => ({ ...atual, ate: valor }));
                    }} />
                  </FormGroup>
                </Col>
                <Col lg="6" md="12" className="cr-advfilters__actions">
                  <Button color="info" disabled={carregandoResumo || Boolean(erroPeriodo)} onClick={() => consultar(filtros)}>
                    {carregandoResumo ? "Carregando..." : "Consultar"}
                  </Button>
                  {erroPeriodo ? <span className="cr-state cr-state--error">{erroPeriodo}</span> : null}
                </Col>
              </Row>
            </section>
          ) : null}

          {consultado && realizado ? <FaixaCobertura cobertura={realizado.cobertura} transportadorasSemEnvio={dados.notas && dados.notas.transportadorasSemEnvio} /> : null}

          {consultado && (totaisR || totaisC) ? (
            <section className="cr-status-section">
              <div className="cr-status-section__title">Resumo do período</div>
              <div className="cr-status-section__subtitle">
                Frete realizado dos CT-es recebidos pelo TMS e frete cotado no GoBID dos pedidos com NF. Os cards abrem o bloco correspondente.
              </div>
              <div className="cr-status-grid">
                <StatusCard label="Frete realizado" tone="info" iconClassName="tim-icons icon-delivery-fast" value={fmtBRL(totaisR ? totaisR.freteRealizado : null)} helper={`${fmtInt(totaisR ? totaisR.ctes : 0)} CT-e(s) válidos no período`} onClick={() => setBloco("realizado")} />
                <StatusCard label="% frete / mercadoria" tone="warning" iconClassName="tim-icons icon-chart-bar-32" value={fmtPct(pctFreteMercadoria)} helper={`${fmtBRL(totaisR ? totaisR.valorMercadoria : null)} em ${fmtInt(totaisR ? totaisR.nfs : 0)} NF(s) de venda`} onClick={() => setBloco("realizado")} />
                <StatusCard label="Realizado ÷ cotado" tone={totaisC && totaisC.realizadoSobreCotado !== null && totaisC.realizadoSobreCotado > 1 ? "danger" : "success"} iconClassName="tim-icons icon-money-coins" value={fmtRazao(totaisC ? totaisC.realizadoSobreCotado : null)} helper={totaisC ? `${fmtBRL(totaisC.freteRealizadoComCotacao)} realizado em ${fmtInt(totaisC.pedidosComCotacao)} pedido(s) cotados em ${fmtBRL(totaisC.freteCotado)}` : loading.cotado ? "Carregando o frete cotado..." : erros.cotado || "Sem frete cotado"} onClick={() => setBloco("cotado")} />
                <StatusCard label="Acima do cotado" tone="danger" iconClassName="tim-icons icon-alert-circle-exc" value={totaisC ? fmtInt(totaisC.acimaDoCotado && totaisC.acimaDoCotado.qtd) : "—"} helper={totaisC ? `${fmtBRL(totaisC.acimaDoCotado && totaisC.acimaDoCotado.valor)} além do cotado · ${fmtInt(totaisC.semCotacao && totaisC.semCotacao.qtd)} pedido(s) sem cotação` : "Pedidos com realizado acima do cotado"} onClick={() => setBloco("cotado")} />
              </div>
            </section>
          ) : null}
        </CardBody>
      </Card>

      {carregandoResumo && !realizado ? (
        <div className="cr-loading">
          <ClipLoader color={AUTOPILOT_COLORS.title} size={32} />
        </div>
      ) : erros.realizado && !realizado ? (
        <Card className="card-everlog-shell" style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}>
          <CardBody style={autopilotBodyStyle}>
            <div className="cr-state cr-state--error">{erros.realizado}</div>
          </CardBody>
        </Card>
      ) : consultado && realizado ? (
        <Card className="card-everlog-shell" style={{ ...autopilotCardStyle, marginTop: "1.25rem", background: "var(--everlog-card-hero-bg)" }}>
          <CardHeader style={autopilotHeaderStyle}>
            <div className="cr-head">
              <div>
                <div style={autopilotEyebrowStyle}>Matriz</div>
                <div style={autopilotTitleStyle}>{(BLOCOS.find((b) => b.key === bloco) || {}).label}</div>
              </div>
              <Button color="success" size="sm" disabled={exportando || !dadosBloco} onClick={handleExport}>
                <i className="tim-icons icon-cloud-download-93" /> {exportando ? "Gerando..." : "Exportar XLSX"}
              </Button>
            </div>
          </CardHeader>
          <CardBody style={autopilotBodyStyle}>
            <div className="cr-blocos">
              {BLOCOS.map((b) => (
                <button key={b.key} type="button" className={`cr-pill${bloco === b.key ? " is-active" : ""}`} onClick={() => setBloco(b.key)}>
                  {b.label}
                </button>
              ))}
            </div>
            <p className="cr-bloco-desc">{DESCRICAO_BLOCO[bloco]}</p>

            {bloco !== "cobertura" ? (
              <div className="cr-controles" style={{ marginTop: 0, marginBottom: "0.9rem" }}>
                <div className="cr-controles__grupo">
                  <span className="cr-controles__rotulo">Quebra</span>
                  {QUEBRAS.map((q) => (
                    <button key={q.key} type="button" className={`cr-pill${quebra === q.key ? " is-active" : ""}`} onClick={() => setQuebra(q.key)}>
                      {q.label}
                    </button>
                  ))}
                  {quebra === "uf" ? (
                    <label className="cr-toggle">
                      <input type="checkbox" checked={porRegiao} onChange={(e) => setPorRegiao(e.target.checked)} /> agrupar por região
                    </label>
                  ) : null}
                </div>
                <div className="cr-controles__grupo">
                  <span className="cr-controles__rotulo">Visão</span>
                  {VISOES.map((v) => (
                    <button key={v.key} type="button" className={`cr-pill${visao === v.key ? " is-active" : ""}`} onClick={() => setVisao(v.key)}>
                      {v.label}
                    </button>
                  ))}
                </div>
                {bloco === "realizado" || bloco === "cotado" ? (
                  <label className="cr-toggle">
                    <input type="checkbox" checked={comparar} onChange={(e) => setComparar(e.target.checked)} /> comparar com o mesmo período do ano anterior
                  </label>
                ) : null}
              </div>
            ) : null}

            {bloco === "tabela" && dadosBloco ? (
              <p className="cr-footnote">
                {fmtInt(dadosBloco.totais && dadosBloco.totais.complementosFora)} CT-e(s) complementar(es) ficaram fora deste bloco (não têm frete simulado).
                {dadosBloco.tolerancia && (dadosBloco.tolerancia.percentual !== null || dadosBloco.tolerancia.valor !== null)
                  ? ` Tolerância do embarcador: ${dadosBloco.tolerancia.percentual !== null ? `${fmtPct(dadosBloco.tolerancia.percentual, 2)}` : "—"} ou ${dadosBloco.tolerancia.valor !== null ? fmtBRL(dadosBloco.tolerancia.valor) : "—"} (o maior), já aplicada na situação gravada.`
                  : ""}
              </p>
            ) : null}
            {bloco === "cotado" && dadosBloco ? (
              <p className="cr-footnote">Acima do cotado = realizado do pedido maior que o cotado + R$ 0,01, sem tolerância. Pedidos sem cotação e NFs sem número de pedido ficam separados e não entram no realizado ÷ cotado.</p>
            ) : null}

            {carregandoBloco ? (
              <div className="cr-loading">
                <ClipLoader color={AUTOPILOT_COLORS.title} size={28} />
              </div>
            ) : erroBloco ? (
              <div className="cr-state cr-state--error">{erroBloco}</div>
            ) : bloco === "cobertura" ? (
              <>
                {filtroCobertura ? (
                  <div className="cr-controles" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                    <span className="cr-pill is-active" style={{ cursor: "default" }}>
                      {filtroCobertura.transportadora
                        ? `Transportadora: ${(dadosBloco && dadosBloco.filtro && dadosBloco.filtro.nomeTransportadora) || `#${filtroCobertura.transportadora}`}`
                        : "Todas as transportadoras"}
                      {filtroCobertura.origem ? ` · Origem: ${ORIGENS_COBERTURA[filtroCobertura.origem]}` : ""}
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        style={{ padding: "0 0 0 0.5rem", margin: 0, color: "inherit" }}
                        title="Limpar filtro do alerta"
                        onClick={limparFiltroCobertura}
                      >
                        ✕
                      </button>
                    </span>
                  </div>
                ) : null}
                <CoberturaTabela dados={dadosBloco} origem={filtroCobertura && filtroCobertura.origem} onExportar={handleExport} exportando={exportando} />
              </>
            ) : dadosBloco ? (
              <MatrizCotadoRealizado
                linhas={linhasMatriz}
                meses={mesesDados}
                visao={visao}
                metricas={metricasBloco}
                compararAnoAnterior={comparar && (bloco === "realizado" || bloco === "cotado")}
                onCelula={abrirDetalhe}
              />
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <DetalheCelulaModal
        aberto={modal.aberto}
        onFechar={fecharModal}
        titulo={modal.titulo}
        subtitulo={modal.subtitulo}
        tipo={modal.tipo}
        loading={modal.loading}
        erro={modal.erro}
        dados={modal.dados}
        mostrarSimulado={modal.mostrarSimulado}
      />
    </div>
  );
};

export default CotadoRealizadoGoBID;
